/**
 * App — root component with providers.
 * QueryClient, Router, and Toast are configured here.
 */
import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { router } from '@/router';
import { AuthGate } from './AuthGate';
import { processGradeRetryQueue } from '@/features/groups/gradeStore';
import { TreadmillExportProvider } from '@/features/trainers/treadmills/TreadmillExportContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000,  // 10 min — data stays fresh while navigating tabs
      gcTime:    60 * 60 * 1000,  // 1h — keep in memory after component unmounts
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export default function App() {
  // On startup, drain any grade saves that failed during a previous session
  useEffect(() => {
    const timer = setTimeout(() => {
      processGradeRetryQueue().catch(() => {});
    }, 5000); // wait 5s for auth to settle before syncing
    return () => clearTimeout(timer);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthGate>
        <TreadmillExportProvider>
          <RouterProvider router={router} />
        </TreadmillExportProvider>
      </AuthGate>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            fontFamily: 'var(--font-sans)',
            borderRadius: 'var(--radius-md)',
          },
        }}
      />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
